import React, {
    useEffect, useRef, useState
} from 'react';
import { getVideoMetadataAsync } from '../../../../general/js/api/endeavor/services/vod-service';
import VideoPlayerWrapper from './video-player-wrapper';
import { GRANTED_ON_SIGN_IN } from '../../../../general/js/api/endeavor/constants/api-constants';
import { useUniqueId } from '../../../../general/js/hooks/useUniqueId';
import { createVodHeartbeat, isPlaybackBlockedError } from '../../../../general/js/api/endeavor/services/heartbeat-service';
import { hasValue } from '../../../../general/js/general-helpers';

const EndeavorPlayer = ({
    content,
    stopPlayer,
    propsMetadata = { isEmpty: true },
}) => {
    const [player, setPlayer] = useState(null);
    const playerRef = useRef(null);
    const eventTypesRef = useRef(null);
    const uniqueId = useUniqueId();

    const [endeavorMetadata, setEndeavorMetadata] = useState({ isEmpty: true });
    const { videoId } = content;

    const playerKey = `dorisPlayer-${content.videoId}-${uniqueId}`;

    useEffect(() => {
        const initPlayer = async () => {
            if (player === null && videoId && playerRef.current) {
                const { addHlsHandler } = await import('@dicetechnology/doris-handler-hls');
                const { addNativeHandler } = await import('@dicetechnology/doris-handler-native');
                const { Player, buildConfig, EventTypes } = await import('@dicetechnology/doris-core');
                const newPlayer = new Player(playerKey, buildConfig(
                    addHlsHandler(),
                    addNativeHandler(),
                ));
                eventTypesRef.current = EventTypes;
                setPlayer(newPlayer);
            }
        };

        initPlayer();
    }, [videoId]);

    useEffect(() => {
        if (stopPlayer && player !== null) {
            try {
                player.pause();
            } catch (exception) {
                throw new Error(`Failed to pause player: ${exception.message}`);
            }
        }
    }, [stopPlayer]);

    useEffect(() => {
        if (player !== null) {
            if (propsMetadata.accessLevel === GRANTED_ON_SIGN_IN) {
                setEndeavorMetadata({ ...propsMetadata });
            } else {
                getVideoMetadataAsync({ videoId })
                    .then((response) => {
                        setEndeavorMetadata(response.metadata);
                        if (!response.metadata.hlsUrl ||
                            endeavorMetadata.accessLevel === GRANTED_ON_SIGN_IN) {
                            return;
                        }

                        player.load(response.metadata.hlsUrl).then(() => {
                            if (!content.autoplay && player !== null) {
                                player.pause();
                            }
                        });
                    });
            }
        }

        return () => {
            if (player !== null) {
                player.destroy();
            }
        };
    }, [videoId, player]);

    useEffect(() => {
        const eventTypes = eventTypesRef.current;

        if (player === null || !hasValue(endeavorMetadata.hlsUrl) || eventTypes === null) {
            return undefined;
        }

        let heartbeat = null;

        const pausePlayer = () => {
            try {
                player.pause();
            } catch (exception) {
                throw new Error(`Failed to pause player: ${exception.message}`);
            }
        };

        const handleHeartbeatError = (error) => {
            if (heartbeat === null || !isPlaybackBlockedError(error)) {
                return;
            }

            heartbeat.stop();
            pausePlayer();
        };

        heartbeat = createVodHeartbeat({ videoId, onError: handleHeartbeatError });

        if (heartbeat === null) {
            return undefined;
        }

        let hasPlayed = false;

        const sendLastHeartbeat = () => {
            if (hasPlayed) {
                heartbeat.stop(true);
            }
        };

        const { unsubscribe } = player.createSubscriptionContext()
            .on(eventTypes.PLAY, () => {
                hasPlayed = true;
                heartbeat.setSessionID();
                heartbeat.start();
            })
            .on(eventTypes.TIME_UPDATE, (_, data) => {
                if (player.getIsPlaying()) {
                    heartbeat.progress = data.currentTime;
                }
            })
            .on(eventTypes.PAUSE, () => heartbeat.stop())
            .on(eventTypes.ENDED, () => sendLastHeartbeat());

        window.addEventListener('pagehide', sendLastHeartbeat);

        return () => {
            window.removeEventListener('pagehide', sendLastHeartbeat);
            unsubscribe();
            sendLastHeartbeat();
        };
    }, [videoId, player, endeavorMetadata.hlsUrl]);

    return (
        <VideoPlayerWrapper
            player={player}
            endeavorMetadata={endeavorMetadata}
            content={content}
            hideCover={content.hideCover}
            playerKey={playerKey}
            playerRef={playerRef}
        />
    );
};

export default EndeavorPlayer;
